import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { KgFlowNodeType } from "../../kg/canvas";
import {
  DATA_SOURCES_SECTION_ID,
  getInspectorTabsForType,
  renderInspectorTabContent,
} from "../../kg/canvas/inspector";
import type { InspectorTabConfig } from "../../kg/canvas/inspector";
import type { KgInspectorIconKey } from "../../kg/canvas/inspector/kgInspectorIcons";
import {
  ARTIFACTS_SECTION_ID,
  DATA_STORY_HEADER_ICON,
  DATA_STORY_TRACE_DEFAULT_EXPANDED,
} from "../../kg/canvas/inspector/dataStoryInspectorConfig";
import {
  ARTIFACT_HEADER_ICON,
} from "../../kg/canvas/inspector/artifactInspectorConfig";
import {
  getInspectorTabsForReport,
  getReportHeaderIcon,
  getReportTraceDefaultExpanded,
  REPORT_SUBTITLE,
  shouldShowReportHeaderSourceLogo,
} from "../../kg/canvas/inspector/reportInspectorConfig";
import type { KgReportSourceType } from "../../../services/kg/kgDrawerApi.types";
import { useKgInspectorTabData } from "../../kg/canvas/hooks/useKgInspectorTabData";
import { EmptyState } from "./KgNodePanel.styles";
import type { KgNodePanelProps } from "./KgNodePanel.types";

const NODE_TYPE_LABEL: Record<KgFlowNodeType, string> = {
  dataSource: "Data source",
  schema: "Schema",
  table: "Table",
  ontology: "Ontology",
  metric: "Metric",
  report: "Report",
  dataStory: "Data story",
  artifact: "Artifact",
  dataModel: "Data model",
};

const PANEL_HEADER_ICON: Partial<Record<KgFlowNodeType, KgInspectorIconKey>> = {
  table: "tablePanel",
  ontology: "ontologyPanel",
  metric: "metricPanel",
  dataModel: "dataModelPanel",
};

export interface KgNodePanelHeaderViewModel {
  headerIcon?: KgInspectorIconKey;
  logoUrl?: string;
  title: string;
  subtitle: string;
  externalUrl?: string;
}

export interface UseKgNodePanelResult {
  isVisible: boolean;
  ariaLabel: string;
  header: KgNodePanelHeaderViewModel;
  tabs: InspectorTabConfig[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  body: React.ReactNode;
  onClose: () => void;
}

const EMPTY_RESULT: UseKgNodePanelResult = {
  isVisible: false,
  ariaLabel: "",
  header: { title: "", subtitle: "" },
  tabs: [],
  activeTab: "overview",
  onTabChange: () => {},
  body: null,
  onClose: () => {},
};

const buildTabBodyFallbacks = (activeTabLabel: string) => ({
  empty: <EmptyState>{`"${activeTabLabel}" tab is coming soon.`}</EmptyState>,
  loading: <EmptyState>Loading...</EmptyState>,
  error: (message: string) => <EmptyState>{message}</EmptyState>,
});

export const useKgNodePanel = ({
  node,
  initialTab,
  onClose,
}: KgNodePanelProps): UseKgNodePanelResult => {
  const tabs = useMemo(() => {
    if (!node) return [];
    const type = node.type as KgFlowNodeType;
    if (type === "report") {
      const sourceType =
        (node.data.reportSourceType as KgReportSourceType | undefined) ??
        undefined;
      return getInspectorTabsForReport(sourceType);
    }
    return getInspectorTabsForType(type);
  }, [node]);

  const defaultTab = initialTab ?? tabs[0]?.id ?? "overview";
  const [activeTab, setActiveTab] = useState<string>(defaultTab);
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    setActiveTab(initialTab ?? tabs[0]?.id ?? "overview");
    setExpandedSections(
      node?.type === "ontology" || node?.type === "metric"
        ? { [DATA_SOURCES_SECTION_ID]: true }
        : node?.type === "report"
          ? getReportTraceDefaultExpanded(node.data.reportSourceType)
          : node?.type === "dataStory"
            ? {
                [ARTIFACTS_SECTION_ID]: true,
                ...DATA_STORY_TRACE_DEFAULT_EXPANDED,
              }
            : {},
    );
  }, [node?.id, tabs, initialTab, node?.type, node?.data.reportSourceType]);

  const nodeType = (node?.type as KgFlowNodeType) ?? null;
  const tabData = useKgInspectorTabData({
    nodeId: node?.id ?? null,
    nodeType,
    activeTab,
    enabled: Boolean(node),
  });

  const toggleSection = useCallback((sectionId: string) => {
    // alert("toggleSection");
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  }, []);

  return useMemo(() => {
    if (!node) return EMPTY_RESULT;

    const type = node.type as KgFlowNodeType;
    if (type === "schema") return EMPTY_RESULT;
    const reportSourceType =
      tabData.reportOverview?.sourceType ??
      (node.data.reportSourceType as KgReportSourceType | undefined);
    const title = node.data.title ?? node.id;
    const activeTabLabel =
      tabs.find((tab) => tab.id === activeTab)?.label ?? activeTab;

    const fallbacks = buildTabBodyFallbacks(activeTabLabel);
    const body = renderInspectorTabContent(
      type,
      activeTab,
      {
        node,
        tabData,
        expandedSections,
        onToggleSection: toggleSection,
      },
      fallbacks.empty,
      fallbacks.loading,
      fallbacks.error,
    );

    return {
      isVisible: true,
      ariaLabel: `${title} details`,
      header: {
        headerIcon:
          type === "report"
            ? getReportHeaderIcon(reportSourceType)
            : type === "dataStory"
              ? DATA_STORY_HEADER_ICON
              : type === "artifact"
                ? ARTIFACT_HEADER_ICON
                : PANEL_HEADER_ICON[type],
        logoUrl:
          type === "report"
            ? shouldShowReportHeaderSourceLogo(reportSourceType) &&
              tabData.reportOverview?.source?.logoUrl
              ? tabData.reportOverview.source.logoUrl
              : undefined
            : node.data.logoUrl,
        title,
        subtitle:
          type === "report" && reportSourceType
            ? REPORT_SUBTITLE[reportSourceType]
            : (NODE_TYPE_LABEL[type] ?? "Node"),
        externalUrl:
          tabData.overview?.externalUrl ??
          tabData.ontologyOverview?.externalUrl ??
          tabData.dataModelOverview?.externalUrl ??
          tabData.artifactOverview?.externalUrl ??
          tabData.dataStoryOverview?.externalUrl ??
          tabData.reportOverview?.externalUrl ??
          tabData.metricOverview?.externalUrl ??
          tabData.tableOverview?.externalUrl ??
          node.data.details?.externalUrl,
      },
      tabs,
      activeTab,
      onTabChange: setActiveTab,
      body,
      onClose,
    };
  }, [
    node,
    tabs,
    activeTab,
    tabData,
    expandedSections,
    toggleSection,
    onClose,
  ]);
};
